import { useQuery } from "@tanstack/react-query";
import { Select } from "@totalenergiescode/mobility-business-rev-design-system";
import {
  Suffix,
  translateField,
  useFormData,
  useFormDataContext,
} from "@totalenergiescode/mobility-business-rev-form-data/components";

import type { SubStructureSelectorProviderInterface } from "@/components/FormData/providers/formData/adapters/divisions";

interface SubStructureSelectorProps {
  fieldId: string;
}

function useDivisions(
  divisionsProvider: SubStructureSelectorProviderInterface,
) {
  return useQuery({
    queryKey: ["divisions"],
    queryFn: async () => await divisionsProvider.getDivisions(""),
  });
}

function SubStructureSelector({ fieldId }: SubStructureSelectorProps) {
  const findFieldById = useFormData((fd) => fd.findFieldById);
  const { dataTestIdPrefix, translatorFor, providers } = useFormDataContext();

  const field = findFieldById(fieldId);

  const { data: divisions = [], isLoading } = useDivisions(providers.divisions);

  const value = field.value.asString();
  const currentSelection = value.value() || value.defaultValue();

  const translator = translatorFor(field);

  const writable = field.writable;
  const errorMessage = field.violations().join(", ");

  const label = translateField(translator, field, Suffix.LABEL);
  const help = translateField(translator, field, Suffix.HELP);
  const helperText = field.hasHelper() ? help.toString() : "";

  const onSubStructureSelectorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    field.setValue(event.target.value);
  };

  return (
    <Select
      dataTestId={`${dataTestIdPrefix}-${field.id()}`}
      type='options'
      options={divisions.map((item) => ({
        id: item.value,
        value: item.value,
        label: item.label,
      }))}
      value={currentSelection}
      label={label.toString()}
      onChange={(event) =>
        onSubStructureSelectorChange(
          event as unknown as React.ChangeEvent<HTMLSelectElement>,
        )
      }
      disabled={!writable || isLoading}
      error={Boolean(errorMessage)}
      errorMessage={errorMessage || ""}
      helperText={helperText}
    />
  );
}

export default SubStructureSelector;
